import type { BasketOptimizer, BasketOptimizerResultRow } from 'state/advisory/basket-optimizer/model';
import type { AppState } from 'state/state.model';
import { SpErrors } from 'utils/errors/spErrors';

function getBasketOptimizerState(state: AppState, tabId: string) {
  if (state.advisory.basketOptimizer === undefined) {
    return undefined;
  }

  return state.advisory.basketOptimizer[tabId];
}

export interface IBasketOptimizerResult {
  result?: BasketOptimizerResultRow[];
  optimizeId?: string;
  errorMessage?: string;
  status?: number;
}
export function getCurrentOptimizeId(state: AppState, tabId: string): string | undefined {
  return getBasketOptimizerState(state, tabId)?.optimizeId;
}

export function getResultRows(state: AppState, tabId: string) {
  return getBasketOptimizerState(state, tabId)?.result;
}
export function getUnderlyingTags(state: AppState, tabId: string) {
  return getBasketOptimizerState(state, tabId)?.underlyingsTags;
}

export function getTargetBasketSize(state: AppState, tabId: string) {
  return getBasketOptimizerState(state, tabId)?.targetBasketSize;
}

export function getIsMutuallyExclusive(state: AppState, tabId: string) {
  return getBasketOptimizerState(state, tabId) && getBasketOptimizerState(state, tabId)!.isMutuallyExclusive;
}

function isBasketLoading(basket: BasketOptimizer) {
  return basket.status === 'loading';
}
export function getIsOptimizerBusy(state: AppState) {
  return Object.values(state.advisory.basketOptimizer).some(isBasketLoading);
}

export function isAdvisoryProcessing(state: AppState, tabId: string) {
  return isBasketOptimizerProcessing(state, tabId);
}

export function getErrors(state: AppState, tabId: string) {
  return getBasketOptimizerState(state, tabId)?.errorMessage ?? '';
}

//////////////////////////
export function getEstimatedComputationTimeInSeconds(state: AppState, tabId: string): number | undefined {
  const basketResult = getBasketOptimizerState(state, tabId);
  return basketResult?.estimatedComputationTimeInSeconds;
}

export function getOptimizationStartDate(state: AppState, tabId: string): Date | undefined {
  const basketResult = getBasketOptimizerState(state, tabId);
  return basketResult?.optimizationStartDate;
}

export function isBasketOptimizerProcessing(state: AppState, quoteId: string): boolean {
  const basketResult = getBasketOptimizerState(state, quoteId);
  return basketResult !== undefined && isBasketLoading(basketResult);
}

export function getCouponFrom(state: AppState, tabId: string) {
  const basketDomain = getBasketOptimizerState(state, tabId);
  return basketDomain?.couponFrom;
}

export function getCouponTo(state: AppState, tabId: string) {
  const basketDomain = getBasketOptimizerState(state, tabId);
  return basketDomain?.couponTo;
}

export function getCouponFromErrors(state: AppState, tabId: string): SpErrors {
  const couponFrom = getCouponFrom(state, tabId);
  const couponTo = getCouponTo(state, tabId);

  const errors = new SpErrors();

  if (couponFrom && couponFrom < 0) {
    errors.addFieldError('couponFrom', 'negativeCouponFrom');
  }

  if (couponFrom && couponTo && couponFrom >= couponTo) {
    errors.addFieldError('couponFrom', 'couponFromHigherOrEqualToCouponTo');
  }

  return errors;
}

export function getCouponToErrors(state: AppState, tabId: string): SpErrors {
  const couponTo = getCouponTo(state, tabId);

  const errors = new SpErrors();

  if (couponTo && couponTo < 0) {
    errors.addFieldError('couponTo', 'negativeCouponTo');
  }

  return errors;
}

export function getCouponsErrors(state: AppState, tabId: string): SpErrors {
  const couponFromErrors = getCouponFromErrors(state, tabId);
  const couponToErrors = getCouponToErrors(state, tabId);
  return new SpErrors(couponFromErrors, couponToErrors);
}
